import RefundDetailViewModel from "../detail/refundDetailViewModel";
import type DistributorRefundDetailResponse from "entities/distributorRefundDetailResponse";
import type { EntityNames } from "config";
import { IPagingFilter } from "@frui.ts/data";
import LocalizationService from "services/localizationService";
import AppRouter from "services/router";
import OptionsService from "services/optionsService";
import RefundRepository from "repositories/refundRepository";
import EventBus from "services/eventBus";
import AlertsViewModel from "../../../alertsViewModel";
import { Router, watchBusy } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import type DistributorRefundResponse from "entities/distributorRefundResponse";
import EnumService from "services/enumService";
import type DistributorPaymentRequestType from "entities/distributorPaymentRequestType";
import RefundsApprovalService from "services/refundsApprovalService";
import PriceListService from "services/priceListService";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

@Router.registerRoute({ route: ":refundId", name: Router.Self })
export default class DistributorRefundDetailViewModel extends RefundDetailViewModel<
  DistributorRefundDetailResponse,
  DistributorRefundResponse
> {
  readonly modelName: EntityNames = "distributorRefundDetailResponse";

  constructor(
    protected itemId: string,
    protected pagingFilter: IPagingFilter,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected refundRepository: RefundRepository,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      optionsService,
      refundRepository,
      eventBus,
      enumService,
      priceListService,
      alertsViewModel
    );
  }

  @watchBusy
  protected async loadDetail(): Promise<DistributorRefundDetailResponse> {
    this.item = await this.refundRepository.getDistributorRefund(this.itemId);

    if (!this.items) {
      this.items = await this.loadList();
    }

    return this.item;
  }

  @watchBusy
  public fetchData() {
    const { limit, offset } = this.pagingFilter;

    return this.refundRepository.getAllDistributorRefunds({ offset, limit });
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code;
  }

  translateChargeType(priceListItemId?: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListItemId);
  }

  translateRequestType(requestType: DistributorPaymentRequestType) {
    return this.localizationService.translateEnum("distributorPaymentRequestType", requestType);
  }

  static Factory({ container }: interfaces.Context) {
    return (refundId: string, pagingFilter: IPagingFilter) =>
      new DistributorRefundDetailViewModel(
        refundId,
        pagingFilter,
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(RefundsApprovalService),
        container.get(OptionsService),
        container.get(RefundRepository),
        container.get(EventBus),
        container.get(EnumService),
        container.get(PriceListService),
        container.get(AlertsViewModel)
      );
  }
}
